import { getSimpleIncomeBookReport } from 'lib/api/reports';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { createSingleDateFilter } from 'lib/common/GlobalSearcher/filterHelpers';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { CustomButtonProps } from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate } from 'lib/functions/utils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import type { CashFlowLine } from '../../../IncomeBook/DailyTable/components/DocumentDialog/utils';
import ProvisionBuilder from '../ProvisionBuilder';

interface HeaderConfig {
  key: keyof CashFlowLine | string;
  label: string;
  type?: 'money' | 'date' | 'text' | 'numeric-id' | 'percentage-soft';
  formatter?: (value: Date, format?: string) => string;
}

interface PortfolioFlowProps {
  onButtonsReady?: (buttons: CustomButtonProps[]) => void;
  isActive?: boolean;
}

interface CreatePortfolioFlowButtonsParams {
  onProvisionClick: () => void;
  triggerSearch: () => Promise<void>;
  filters: Filter[];
  loading: boolean;
  dispatch: Dispatch;
}

const createPortfolioFlowButtons = ({
  onProvisionClick,
  triggerSearch,
  filters,
  loading,
  dispatch,
}: CreatePortfolioFlowButtonsParams): CustomButtonProps[] => {
  return [
    {
      action: onProvisionClick,
      name: 'Provisionar',
      color: 'primary',
      disabled: false,
    },
    {
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
  ];
};

const getToday = (): string => {
  return new Date().toISOString().split('T')[0];
};

const PortfolioFlow: React.FC<PortfolioFlowProps> = ({
  onButtonsReady,
  isActive = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState<any[]>([]);
  const dispatch = useDispatch();
  const portfolioFlowDateDefault = getToday();
  const [portfolioFlowDate, setPortfolioFlowDate] = useState(
    portfolioFlowDateDefault,
  );
  const [portfolioFlowBusinessIdentifier, setPortfolioFlowBusinessIdentifier] =
    useState('');
  const [portfolioFlowBusinessName, setPortfolioFlowBusinessName] =
    useState('');
  const [portfolioFlowFolio, setPortfolioFlowFolio] = useState('');
  const [openProvisionBuilder, setOpenProvisionBuilder] = useState(false);
  const [calculatedCutoffDate, setCalculatedCutoffDate] = useState<
    string | null
  >(null);

  const generateQuery = () => {
    let query = '';
    if (portfolioFlowDate) {
      query += `endDate=${portfolioFlowDate}&`;
    }
    if (portfolioFlowBusinessIdentifier)
      query += `businessIdentifier=${portfolioFlowBusinessIdentifier}&`;
    if (portfolioFlowBusinessName)
      query += `businessName=${portfolioFlowBusinessName.replace('&', '%26')}&`;
    if (portfolioFlowFolio) query += `folio=${portfolioFlowFolio}&`;
    query += `showLines=true&`;
    return query;
  };

  const triggerSearch = useCallback(async () => {
    setLoading(true);
    try {
      const query = generateQuery();
      const response = await getSimpleIncomeBookReport(dispatch, query);
      if (Array.isArray(response) && response.length > 0) {
        const allLines = response.flatMap((item) => item.lines || []);
        const linesWithCutoffDate = allLines.map((line) => ({
          ...line,
          cutoffDate: calculatedCutoffDate || portfolioFlowDate,
        }));
        setLines(linesWithCutoffDate);
      } else {
        setLines([]);
      }
    } catch (error) {
      console.error('Error fetching portfolio flow data:', error);
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    portfolioFlowDate,
    portfolioFlowBusinessIdentifier,
    portfolioFlowBusinessName,
    portfolioFlowFolio,
    calculatedCutoffDate,
  ]);

  const hasLoadedInitially = useRef(false);

  useEffect(() => {
    if (isActive && !hasLoadedInitially.current) {
      hasLoadedInitially.current = true;
      void triggerSearch();
    }
  }, [isActive, triggerSearch]);

  const handleDateChange = useCallback((newDate: string | null) => {
    setPortfolioFlowDate(newDate || getToday());
  }, []);

  const portfolioFlowFilters: Filter[] = useMemo(
    () => [
      createSingleDateFilter(
        'portfolioFlowDate',
        'Al día de',
        portfolioFlowDate,
        handleDateChange,
      ),
      {
        label: 'Nombre cliente',
        type: 'autocomplete',
        value: portfolioFlowBusinessName,
        key: 'businessName',
        setter: setPortfolioFlowBusinessName,
      },
      {
        label: 'Rut cliente',
        type: 'text',
        value: portfolioFlowBusinessIdentifier,
        setter: setPortfolioFlowBusinessIdentifier,
      },
      {
        label: 'Folio',
        type: 'text',
        value: portfolioFlowFolio,
        setter: setPortfolioFlowFolio,
      },
    ],
    [
      portfolioFlowDate,
      portfolioFlowBusinessName,
      portfolioFlowBusinessIdentifier,
      portfolioFlowFolio,
      handleDateChange,
    ],
  );

  const buttons = useMemo(
    () =>
      createPortfolioFlowButtons({
        onProvisionClick: () => setOpenProvisionBuilder(true),
        triggerSearch,
        filters: portfolioFlowFilters,
        loading,
        dispatch,
      }),
    [triggerSearch, portfolioFlowFilters, loading, dispatch],
  );

  const prevButtonsRef = useRef<string>();
  useEffect(() => {
    if (onButtonsReady) {
      const buttonsKey = JSON.stringify({
        buttons: buttons.map((b) => ({
          name: b.name,
          color: b.color,
          disabled: b.disabled,
        })),
        portfolioFlowDate,
        portfolioFlowBusinessName,
        portfolioFlowBusinessIdentifier,
        portfolioFlowFolio,
      });
      if (buttonsKey !== prevButtonsRef.current) {
        prevButtonsRef.current = buttonsKey;
        onButtonsReady(buttons);
      }
    }
  }, [
    onButtonsReady,
    buttons,
    portfolioFlowDate,
    portfolioFlowBusinessName,
    portfolioFlowBusinessIdentifier,
    portfolioFlowFolio,
  ]);

  const portfolioFlowHeaders: HeaderConfig[] = [
    { key: 'requestTypeTranslated', label: 'Tipo de solicitud', type: 'text' },
    { key: 'documentType', label: 'Tipo documento', type: 'text' },
    { key: 'businessName', label: 'Nombre empresa', type: 'text' },
    { key: 'businessIdentifier', label: 'RUT', type: 'text' },
    { key: 'requestId', label: 'ID/Folio', type: 'text' },
    { key: 'stakeholderName', label: 'Deudor', type: 'text' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor', type: 'text' },
    { key: 'amountInWallet', label: 'Documento', type: 'money' },
    { key: 'amountInWalletNet', label: 'Documento Neto', type: 'money' },
    { key: 'payment', label: 'Abono', type: 'money' },
    { key: 'realAmountInWallet', label: 'Total colocado', type: 'money' },
    {
      key: 'realAmountInWalletNet',
      label: 'Total colocado neto',
      type: 'money',
    },
    {
      key: 'operationDate',
      label: 'Fecha de operacion',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'originalPaymentDate',
      label: 'Vencimiento original',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'finalPaymentDate',
      label: 'Vencimiento final',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    { key: 'fundName', label: 'Fondo', type: 'text' },
    { key: 'status', label: 'Estado', type: 'text' },
    ...(calculatedCutoffDate
      ? [
          {
            key: 'cutoffDate',
            label: 'Fecha de corte',
            type: 'date' as const,
            formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
          },
          {
            key: 'diasMora',
            label: 'Días de mora',
            type: 'text' as const,
          },
          {
            key: 'tramo',
            label: 'Tramo',
            type: 'text' as const,
          },
          {
            key: 'provisionPercentage',
            label: 'PRO %',
            type: 'percentage-soft' as const,
          },
          {
            key: 'provisionAmount',
            label: 'Monto de Provisión',
            type: 'money' as const,
          },
        ]
      : []),
    { key: 'userOwnerName', label: 'Ejecutivo', type: 'text' },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <div style={{ overflowX: 'scroll', padding: '8px' }}>
        <EnhancedTable
          dataHeaders={portfolioFlowHeaders}
          data={lines}
          exportToFile="xlsx"
          rowsPerPageDefault={25}
          finalSumRow
          showItemColumn
        />
      </div>
      <ProvisionBuilder
        open={openProvisionBuilder}
        setOpen={setOpenProvisionBuilder}
        lines={lines}
        onCalculate={(calculatedLines, cutoffDate) => {
          setCalculatedCutoffDate(cutoffDate);
          setLines(calculatedLines);
          setOpenProvisionBuilder(false);
        }}
      />
    </div>
  );
};

export default PortfolioFlow;
